import "./styles/wyrm.css";
import type { WyrmDef } from "../rare/wyrmApi";
import { bramStill } from "./guide/bram/bramView";
import type { BossBar } from "./bossBar";
import { motionOk } from "./kit";
import { portraitUrl } from "./portraits";
import { WYRM_ARRIVAL, WYRM_COPY, WYRM_ESCAPE, WYRM_LINES } from "./wyrmModel";

/**
 * The Starfall Wyrm's DOM (M40), a size up from the rare's (rareUi.ts): the arrival banner on a prismatic ribbon that
 * shimmers, its portrait in a ring of starlight, a LEGENDARY tag and Captain Bram's alarmed line; the boss bar as a
 * prismatic "legend" bar; the escape ribbon; Bram's farewell when it is let go. Transform/opacity animations only and
 * pointer-transparent; nothing here pauses anything (the choice sheet is wyrmChoice.ts, the reveal wyrmReveal.ts).
 * Bram is his still render (bramStill), never a second WebGL context mid-battle.
 */

const css = (hex: number): string => `#${hex.toString(16).padStart(6, "0")}`;

/** Sets the Wyrm's three colours (--wy-a lilac, --wy-b rose-gold, --wy-c cyan) on `el`. */
export function paintWyrm(el: HTMLElement, def: WyrmDef): void {
  el.style.setProperty("--wy-a", css(def.sparkA));
  el.style.setProperty("--wy-b", css(def.sparkB));
  el.style.setProperty("--wy-c", css(def.sparkC));
}

/** The Wyrm's portrait `<img>` (its HQ icon), or a star glyph while the icon is missing. */
export function wyrmFaceHtml(): string {
  const pic = portraitUrl("wyrm");
  return pic ? `<img decoding="sync" src="${pic}" alt="" onerror="this.remove()"><i class="wy-glyph">✦</i>` : `<i class="wy-glyph">✦</i>`;
}

function bramHtml(line: string): string { return `<div class="rb-bram wb-bram"><div class="rb-bust"></div><div class="rb-say">${line}</div></div>`; }

/**
 * "THE STARFALL WYRM DESCENDS!" (or, on a paid retry, "IT RETURNS!"): spinning prismatic rays and a burst of stars
 * behind the portrait, the title slammed in on the dive's downbeat (`SlamMs`), what is at stake, then Bram's line.
 */
export class WyrmBanner {
  private el: HTMLDivElement | null = null;
  private timer = 0;

  constructor(private readonly parent: HTMLElement) {}

  /** `low`: in the lower half of the screen (the beast dives in over the upper half), else the upper. */
  show(def: WyrmDef, retry: boolean, sub: string, low = false): void {
    this.hide();
    const A = WYRM_ARRIVAL, slam = retry ? A.ReturnSlamMs : A.SlamMs;
    const el = document.createElement("div");
    el.className = `wyrm-banner${retry ? " retry" : ""}${low ? " low" : ""}`;
    paintWyrm(el, def);
    const stars = Array.from({ length: 10 }, (_, i) => `<i style="--i:${i}"></i>`).join("");
    el.innerHTML = `<div class="wb-rays"></div><div class="wb-stars">${stars}</div><div class="wb-face">${wyrmFaceHtml()}</div>`
      + `<div class="wb-title disp"><span>${retry ? WYRM_COPY.returnTitle : WYRM_COPY.arrivalTitle(def.name)}</span></div>`
      + `<div class="plate wb-sub">${sub}</div>`
      + (retry ? "" : bramHtml(WYRM_LINES.spawn));
    el.querySelector(".rb-bust")?.append(bramStill("alarm", 74));
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      const q = (s: string) => el.querySelector<HTMLElement>(s);
      q(".wb-face")!.animate([{ transform: "translateY(-160px) scale(0.3)", opacity: 0 }, { transform: "translateY(8px) scale(1.18)", opacity: 1, offset: 0.75 }, { transform: "none", opacity: 1 }], { duration: slam, easing: "cubic-bezier(.5,0,.6,1.4)", fill: "backwards" });
      q(".wb-title")!.animate([{ transform: "scale(2.8)", opacity: 0 }, { transform: "scale(0.92)", opacity: 1, offset: 0.55 }, { transform: "none", opacity: 1 }], { duration: 460, delay: slam - 120, easing: "ease-out", fill: "backwards" });
      q(".wb-sub")!.animate([{ transform: "translateY(10px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 300, delay: slam + 320, fill: "backwards" });
      q(".wb-bram")?.animate([{ transform: "translateX(-40px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 360, delay: A.BramMs, easing: "cubic-bezier(.2,1.3,.4,1)", fill: "backwards" });
    }
    this.timer = window.setTimeout(() => this.leave(), retry ? A.ReturnLeaveMs : A.LeaveMs);
  }

  private leave(): void {
    const el = this.el;
    if (!el) return;
    if (!motionOk() || typeof el.animate !== "function") { this.hide(); return; }
    el.animate([{ opacity: 1 }, { opacity: 0, transform: "translate(-50%, -54%) scale(0.96)" }], { duration: 420, easing: "ease-in", fill: "forwards" }).onfinish = () => this.hide();
  }

  get shown(): boolean { return this.el !== null; }
  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}

/** The boss bar as the Wyrm's "legend" bar: a prismatic plate with its name, a LEGEND tag and a flowing prismatic fill. */
export function legendBar(bar: BossBar, def: WyrmDef): void {
  const root = bar.root;
  paintWyrm(root, def);
  root.classList.remove("rare");
  root.classList.add("legend");
  const span = root.querySelector(".bb-name span");
  if (span) span.textContent = def.name;
  const tag = root.querySelector<HTMLElement>(".bb-tag");
  if (tag) tag.textContent = "LEGEND";
  const img = root.querySelector<HTMLImageElement>(".bb-face img");
  const url = portraitUrl("wyrm");
  if (img && url) { img.src = url; img.classList.remove("hidden"); }
}

/** Back to a plain boss bar after the Wyrm (the level's own boss, a rebuilt level). */
export function unlegendBar(bar: BossBar): void {
  bar.root.classList.remove("legend");
  const tag = bar.root.querySelector<HTMLElement>(".bb-tag");
  if (tag) tag.textContent = "ENRAGED";
}

/** "THE STARFALL WYRM ESCAPES!": its portrait rising away on a dusk-violet ribbon and Bram's line; the choice follows. */
export class WyrmEscape {
  private el: HTMLDivElement | null = null;
  private timer = 0;
  constructor(private readonly parent: HTMLElement) {}

  show(def: WyrmDef): void {
    this.hide();
    const el = document.createElement("div");
    el.className = "wyrm-escape";
    paintWyrm(el, def);
    el.innerHTML = `<div class="we-face">${wyrmFaceHtml()}</div><div class="wb-title we-title disp"><span>${WYRM_COPY.escapeTitle}</span></div>`
      + `<div class="plate we-sub">${WYRM_COPY.escapeSub}</div>${bramHtml(WYRM_LINES.escape)}`;
    el.querySelector(".rb-bust")?.append(bramStill("alarm", 64));
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      el.querySelector<HTMLElement>(".we-title")!.animate([{ transform: "scale(1.9)", opacity: 0 }, { transform: "scale(0.95)", opacity: 1, offset: 0.6 }, { transform: "none", opacity: 1 }], { duration: 480, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".we-face")!.animate([{ transform: "none" }, { transform: "translateY(-26px) scale(0.86)" }], { duration: WYRM_ESCAPE.RibbonHoldMs, easing: "ease-in", fill: "forwards" });
    }
    this.timer = window.setTimeout(() => this.hide(), WYRM_ESCAPE.RibbonHoldMs);
  }

  get shown(): boolean { return this.el !== null; }
  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}

/** Let go: Bram's bittersweet farewell, low over the map, while the night lifts. */
export class WyrmFarewell {
  private el: HTMLDivElement | null = null;
  private timer = 0;
  static readonly HoldMs = 3600;
  constructor(private readonly parent: HTMLElement) {}

  show(def: WyrmDef): void {
    this.hide();
    const el = document.createElement("div");
    el.className = "wyrm-farewell";
    paintWyrm(el, def);
    el.innerHTML = bramHtml(WYRM_LINES.release);
    el.querySelector(".rb-bust")?.append(bramStill("neutral", 64));
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") el.animate([{ transform: "translateY(30px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 380, easing: "cubic-bezier(.2,1.3,.4,1)", fill: "backwards" });
    this.timer = window.setTimeout(() => this.hide(), WyrmFarewell.HoldMs);
  }

  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}
